import React, { useState } from "react";
import PropTypes from "prop-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2 } from "lucide-react";
import warrantyApi from "@/api/warrantyApi";

const ResolveClaimForm = ({ claim, onOpenChange, onDone }) => {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState("");

  const resolveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        notes: notes.trim() || undefined,
      };
      const res = await warrantyApi.resolve(claim.id, payload);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success("Warranty claim resolved successfully");
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }

      toast.info("Claim resolved. You can print the completion certificate.", {
        action: {
          label: "Print Certificate",
          onClick: () =>
            warrantyApi.openPdfReport(warrantyApi.pdf.completion(claim.id)),
        },
        duration: 8000,
      });

      onOpenChange(false);
      if (onDone) onDone(data);
    },
    onError: (error) => {
      const msg = error.response?.data?.message || "Failed to resolve claim";
      toast.error(msg);
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    resolveMutation.mutate();
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div>
            <DialogTitle>Resolve Claim #{claim.claim_number}</DialogTitle>
            <DialogDescription>
              Record handover and finalize remedy. Returned items will be moved to refurbished or damaged inventory.
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4 py-2">
        <div className="space-y-2">
          <Label htmlFor="resolve_notes">
            Resolution &amp; Handover Notes{" "}
            <span className="text-xs text-muted-foreground font-normal">
              (Optional)
            </span>
          </Label>
          <Textarea
            id="resolve_notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Replacement handed over to customer in working condition; customer satisfied..."
            rows={4}
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={resolveMutation.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={resolveMutation.isPending}>
            {resolveMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Resolving...
              </>
            ) : (
              "Mark as Resolved"
            )}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
};

ResolveClaimForm.propTypes = {
  claim: PropTypes.object.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onDone: PropTypes.func,
};

const ResolveClaimModal = ({ open, onOpenChange, claim, onDone }) => {
  if (!claim) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        {open && (
          <ResolveClaimForm
            key={claim.id || "resolve"}
            claim={claim}
            onOpenChange={onOpenChange}
            onDone={onDone}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

ResolveClaimModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  claim: PropTypes.object,
  onDone: PropTypes.func,
};

export default ResolveClaimModal;
